import React, { useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Box, Avatar, CircularProgress, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    LockOutlined,
    BusinessOutlined,
    EventOutlined,
} from '@mui/icons-material';
import MDTypography from 'components/MDTypography';

/* -------------------------------------------------------------------------- */
/* Config                                                                     */
/* -------------------------------------------------------------------------- */

// Same stages as DealDetail's `dealStages`
export const DEAL_STAGES = [
    'Qualification',
    'Needs Analysis',
    'Proposal',
    'Negotiation',
    'Closed Won',
    'Closed Lost',
];

// Same rule as DealDetail's isFinalStage: nothing moves out of these
export const LOCKED_DEAL_STAGES = ['Closed Won', 'Closed Lost'];

const STAGE_CONFIG = {
    Qualification: { accent: '#3b82f6', tint: 'rgb(240, 245, 255)' },
    'Needs Analysis': { accent: '#8b5cf6', tint: 'rgb(246, 242, 255)' },
    Proposal: { accent: '#f59e0b', tint: 'rgb(255, 247, 240)' },
    Negotiation: { accent: '#0ea5e9', tint: 'rgb(238, 248, 255)' },
    'Closed Won': { accent: '#10b981', tint: 'rgb(240, 255, 245)' },
    'Closed Lost': { accent: '#ef4444', tint: 'rgb(255, 240, 240)' },
};

const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#14b8a6', '#f97316', '#ec4899', '#8b5cf6'];

const INK = '#1e293b';
const MUTED = '#64748b';
const FAINT = '#94a3b8';
const BORDER = 'rgba(15, 23, 42, 0.07)';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// null, undefined, "" and "   " all count as "no value"
const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';

const formatCardDate = (value) => {
    if (!hasValue(value)) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isOverdue = (value, stage) => {
    if (!hasValue(value) || LOCKED_DEAL_STAGES.includes(stage)) return false;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
};

const getInitials = (name) => {
    const parts = String(name || '?').trim().split(/\s+/);
    return `${parts[0]?.charAt(0) || '?'}${parts[1]?.charAt(0) || ''}`.toUpperCase();
};

const avatarColor = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];

const scrollbar = (size) => ({
    '&::-webkit-scrollbar': { width: size, height: size },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(100, 116, 139, 0.28)',
        borderRadius: size,
        '&:hover': { background: 'rgba(100, 116, 139, 0.5)' },
    },
});

/* -------------------------------------------------------------------------- */
/* Card                                                                       */
/* -------------------------------------------------------------------------- */

const DealCardContent = ({
                             deal,
                             accent,
                             locked,
                             isDragging,
                             isUpdating,
                             accountName,
                             formatCurrency,
                             onClick,
                         }) => {
    const hasName = hasValue(deal.name);
    const title = hasName ? deal.name : `Deal ${deal.id}`;
    const owner = hasValue(deal.assigned_to_name) ? deal.assigned_to_name : 'Unassigned';
    const numericValue = Number(deal.value);
    const hasAmount = hasValue(deal.value) && Number.isFinite(numericValue) && numericValue > 0;
    const closeDate = formatCardDate(deal.estimated_closure_date);
    const overdue = isOverdue(deal.estimated_closure_date, deal.stage);
    const updatedOn = formatCardDate(deal.updated_at);

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '14px',
                border: '1px solid',
                borderColor: isDragging ? alpha(accent, 0.55) : BORDER,
                boxShadow: isDragging
                    ? '0 14px 30px rgba(15, 23, 42, 0.2)'
                    : '0 1px 2px rgba(15, 23, 42, 0.04)',
                // Tilt on the inner element so it never fights the transform
                // react-beautiful-dnd puts on the outer wrapper.
                transform: isDragging ? 'rotate(1.5deg)' : 'none',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                cursor: locked ? 'pointer' : isDragging ? 'grabbing' : 'grab',
                overflow: 'hidden',
                '&:hover': isDragging
                    ? {}
                    : {
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.09)',
                        borderColor: alpha(accent, 0.4),
                    },
            }}
        >
            {isUpdating && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.72)',
                        backdropFilter: 'blur(1px)',
                    }}
                >
                    <CircularProgress size={22} />
                </Box>
            )}

            {/* Header: avatar, name + account, lock */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <Avatar
                    sx={{
                        width: 34,
                        height: 34,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        bgcolor: alpha(avatarColor(deal.id), 0.14),
                        color: avatarColor(deal.id),
                        flexShrink: 0,
                    }}
                >
                    {getInitials(title)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <MDTypography
                        variant="body2"
                        fontWeight="medium"
                        title={title}
                        sx={{
                            fontSize: '0.9rem',
                            lineHeight: 1.35,
                            color: INK,
                            fontStyle: hasName ? 'normal' : 'italic',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {title}
                    </MDTypography>

                    {hasValue(accountName) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0 }}>
                            <BusinessOutlined sx={{ fontSize: 14, color: FAINT, flexShrink: 0 }} />
                            <MDTypography
                                variant="caption"
                                title={accountName}
                                sx={{
                                    color: MUTED,
                                    fontSize: '0.78rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {accountName}
                            </MDTypography>
                        </Box>
                    )}
                </Box>

                {locked && (
                    <Tooltip title={`${deal.stage} deals can't be moved`} placement="top">
                        <LockOutlined sx={{ fontSize: 16, color: FAINT, flexShrink: 0, mt: '2px' }} />
                    </Tooltip>
                )}
            </Box>

            {/* Value */}
            <Box sx={{ mt: 1.5 }}>
                {hasAmount ? (
                    <MDTypography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ fontSize: '0.95rem', color: INK, fontVariantNumeric: 'tabular-nums' }}
                    >
                        {formatCurrency(numericValue, deal.currency)}
                    </MDTypography>
                ) : (
                    <MDTypography variant="caption" sx={{ color: FAINT, fontStyle: 'italic' }}>
                        No value set
                    </MDTypography>
                )}
            </Box>

            {/* Estimated closure date */}
            {closeDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minWidth: 0 }}>
                    <EventOutlined sx={{ fontSize: 16, color: overdue ? '#ef4444' : FAINT, flexShrink: 0 }} />
                    <MDTypography
                        variant="caption"
                        sx={{
                            fontSize: '0.78rem',
                            color: overdue ? '#ef4444' : MUTED,
                            fontWeight: overdue ? 600 : 400,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {overdue ? `Overdue · ${closeDate}` : `Closes ${closeDate}`}
                    </MDTypography>
                </Box>
            )}

            {/* Lost reason (only when the list payload carries it) */}
            {deal.stage === 'Closed Lost' && hasValue(deal.closed_lost_comment) && (
                <MDTypography
                    variant="caption"
                    title={deal.closed_lost_comment}
                    sx={{
                        display: 'block',
                        mt: 1,
                        color: MUTED,
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    “{deal.closed_lost_comment}”
                </MDTypography>
            )}

            {/* Footer: ID · updated date on the left, owner avatar on the right */}
            <Box
                sx={{
                    mt: 1.5,
                    pt: 1.25,
                    borderTop: `1px solid ${BORDER}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    minHeight: 26,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{ color: MUTED, fontSize: '0.72rem', lineHeight: 1, flexShrink: 0 }}
                    >
                        #{deal.id}
                    </MDTypography>
                    {updatedOn && (
                        <>
                            <Box
                                component="span"
                                sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: FAINT, flexShrink: 0 }}
                            />
                            <Tooltip title={`Updated ${updatedOn}`} placement="top" arrow>
                                <MDTypography
                                    variant="caption"
                                    sx={{ color: MUTED, fontSize: '0.72rem', lineHeight: 1, whiteSpace: 'nowrap' }}
                                >
                                    {updatedOn}
                                </MDTypography>
                            </Tooltip>
                        </>
                    )}
                </Box>

                <Tooltip title={owner === 'Unassigned' ? 'Unassigned' : `Assigned to ${owner}`} placement="top" arrow>
                    <Avatar
                        src={deal.assigned_to_avatar || undefined}
                        alt={owner}
                        sx={{
                            width: 26,
                            height: 26,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: owner === 'Unassigned' ? '#e2e8f0' : alpha(avatarColor(deal.assigned_to), 0.16),
                            color: owner === 'Unassigned' ? MUTED : avatarColor(deal.assigned_to),
                            flexShrink: 0,
                            ml: 'auto',
                        }}
                    >
                        {owner.charAt(0).toUpperCase()}
                    </Avatar>
                </Tooltip>
            </Box>
        </Box>
    );
};

/* -------------------------------------------------------------------------- */
/* Board                                                                      */
/* -------------------------------------------------------------------------- */

const DealsKanbanBoard = ({
                              deals,
                              accounts = {},          // { [accountId]: account } -- same map Deals.js already builds
                              formatCurrency,         // from useCurrency(), passed in to avoid a path dependency
                              onDragEnd,
                              onDealClick,
                              updatingDealId,
                          }) => {
    // Group once per deals change
    const columns = useMemo(() => {
        const grouped = DEAL_STAGES.reduce((acc, s) => ({ ...acc, [s]: [] }), {});
        (deals || []).forEach((deal) => {
            const stage = grouped[deal?.stage] ? deal.stage : DEAL_STAGES[0];
            grouped[stage].push(deal);
        });
        return grouped;
    }, [deals]);

    // Column total, shown only when every deal in the column shares one currency
    // (summing mixed currencies would be a wrong number).
    const columnTotals = useMemo(() => {
        const totals = {};
        DEAL_STAGES.forEach((stage) => {
            const list = columns[stage];
            const currencies = new Set(list.map((d) => d.currency || ''));
            const sum = list.reduce((s, d) => s + (Number(d.value) || 0), 0);
            totals[stage] =
                list.length > 0 && currencies.size === 1 && sum > 0
                    ? formatCurrency(sum, list[0].currency)
                    : null;
        });
        return totals;
    }, [columns, formatCurrency]);

    // Dropping back into the same column is a no-op, so skip the work.
    const handleDragEnd = useCallback(
        (result) => {
            const { source, destination } = result;
            if (!destination || source.droppableId === destination.droppableId) return;
            onDragEnd?.(result);
        },
        [onDragEnd]
    );

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 2,
                    p: 2,
                    height: 'calc(100vh - 320px)',
                    minHeight: 440,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    ...scrollbar('8px'),
                }}
            >
                {DEAL_STAGES.map((stage) => {
                    const { accent, tint } = STAGE_CONFIG[stage];
                    const stageDeals = columns[stage];
                    const total = columnTotals[stage];

                    return (
                        <Box
                            key={stage}
                            sx={{
                                flex: '1 1 270px',
                                minWidth: 270,
                                maxWidth: 380,
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                backgroundColor: tint,
                                borderRadius: '14px',
                                border: `1px solid ${BORDER}`,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Column header */}
                            <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: accent }} />
                                        <MDTypography variant="h6" sx={{ fontSize: '0.9rem', color: INK }}>
                                            {stage}
                                        </MDTypography>
                                    </Box>
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="medium"
                                        sx={{
                                            minWidth: 24,
                                            textAlign: 'center',
                                            px: 1,
                                            py: '2px',
                                            borderRadius: '10px',
                                            color: accent,
                                            backgroundColor: alpha(accent, 0.12),
                                        }}
                                    >
                                        {stageDeals.length}
                                    </MDTypography>
                                </Box>
                                {total && (
                                    <MDTypography
                                        variant="caption"
                                        sx={{ display: 'block', mt: 0.5, ml: 2.25, color: MUTED, fontSize: '0.75rem' }}
                                    >
                                        {total}
                                    </MDTypography>
                                )}
                            </Box>

                            {/* Droppable body */}
                            <Droppable droppableId={stage}>
                                {(dropProvided, dropSnapshot) => (
                                    <Box
                                        ref={dropProvided.innerRef}
                                        {...dropProvided.droppableProps}
                                        sx={{
                                            flex: 1,
                                            minHeight: 120,
                                            overflowY: 'auto',
                                            px: 1.25,
                                            pb: 1,
                                            pt: 0.5,
                                            transition: 'background-color 0.2s ease',
                                            backgroundColor: dropSnapshot.isDraggingOver ? alpha(accent, 0.08) : 'transparent',
                                            ...scrollbar('6px'),
                                        }}
                                    >
                                        {stageDeals.length === 0 && !dropSnapshot.isDraggingOver && (
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    py: 4,
                                                    textAlign: 'center',
                                                    borderRadius: '10px',
                                                    border: '1px dashed rgba(100, 116, 139, 0.3)',
                                                }}
                                            >
                                                <MDTypography variant="caption" sx={{ color: FAINT }}>
                                                    No deals here
                                                </MDTypography>
                                            </Box>
                                        )}

                                        {stageDeals.map((deal, index) => {
                                            const locked = LOCKED_DEAL_STAGES.includes(deal.stage);
                                            const isUpdating = String(updatingDealId) === String(deal.id);

                                            return (
                                                <Draggable
                                                    key={deal.id}
                                                    draggableId={String(deal.id)}
                                                    index={index}
                                                    isDragDisabled={locked || isUpdating}
                                                >
                                                    {(dragProvided, dragSnapshot) => (
                                                        // Plain wrapper: dnd owns its transform/transition.
                                                        <div
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                            style={{ ...dragProvided.draggableProps.style, marginBottom: 10 }}
                                                        >
                                                            <DealCardContent
                                                                deal={deal}
                                                                accent={accent}
                                                                locked={locked}
                                                                isDragging={dragSnapshot.isDragging}
                                                                isUpdating={isUpdating}
                                                                accountName={deal.account_name || accounts[deal.account_id]?.name}
                                                                formatCurrency={formatCurrency}
                                                                onClick={() => onDealClick?.(deal)}
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {dropProvided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </Box>
                    );
                })}
            </Box>
        </DragDropContext>
    );
};

export default DealsKanbanBoard;